/**
 * useOnboarding - Hook for managing onboarding tooltip state
 *
 * Tracks which tooltips have been seen using localStorage for granular tracking
 * and syncs completion status to the database for persistence.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../lib/api';

// Tooltip IDs that are part of onboarding
export const ONBOARDING_TOOLTIPS = {
  DASHBOARD: 'dashboard',
  NEW_SESSION: 'new-session',
  SESSIONS: 'sessions',
} as const;

export type OnboardingTooltipId = typeof ONBOARDING_TOOLTIPS[keyof typeof ONBOARDING_TOOLTIPS];

const STORAGE_KEY = 'demopath-onboarding-seen';
const SKIPPED_KEY = 'demopath-onboarding-skipped';

interface UserResponse {
  user: {
    id: string;
    onboardingCompletedAt: string | null;
  };
}

interface UseOnboardingReturn {
  // Whether onboarding is fully complete (all tips seen or skipped)
  isComplete: boolean;
  // Whether a specific tooltip should show
  shouldShowTooltip: (id: OnboardingTooltipId) => boolean;
  // Mark a tooltip as seen
  dismissTooltip: (id: OnboardingTooltipId) => void;
  // Skip all remaining tooltips
  skipAll: () => void;
  // Check if this is the last unseen tooltip
  isLastTooltip: (id: OnboardingTooltipId) => boolean;
  // Loading state
  isLoading: boolean;
}

export function useOnboarding(): UseOnboardingReturn {
  const { get, patch } = useApi();
  const [seenTooltips, setSeenTooltips] = useState<Set<string>>(new Set());
  const [isSkipped, setIsSkipped] = useState(false);
  const [isServerComplete, setIsServerComplete] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load initial state from localStorage and server
  useEffect(() => {
    // Load from localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setSeenTooltips(new Set(JSON.parse(stored)));
      }
      const skipped = localStorage.getItem(SKIPPED_KEY);
      if (skipped === 'true') {
        setIsSkipped(true);
      }
    } catch {
      // Ignore localStorage errors
    }

    // Check server status
    async function checkServerStatus() {
      try {
        const response = await get<UserResponse>('/users/me');
        setIsServerComplete(response.user.onboardingCompletedAt !== null);
      } catch {
        // If we can't reach server, continue with localStorage only
        setIsServerComplete(false);
      } finally {
        setIsLoading(false);
      }
    }

    checkServerStatus();
  }, [get]);

  // Persist seen tooltips to localStorage
  const persistSeen = useCallback((tooltips: Set<string>) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...tooltips]));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // All tooltip IDs as array
  const allTooltipIds = useMemo(
    () => Object.values(ONBOARDING_TOOLTIPS),
    []
  );

  // Check if all tooltips have been seen
  const allSeen = useMemo(() => {
    return allTooltipIds.every((id) => seenTooltips.has(id));
  }, [seenTooltips, allTooltipIds]);

  // Is onboarding complete?
  const isComplete = useMemo(() => {
    // Server says complete, or all seen locally, or skipped
    return isServerComplete === true || allSeen || isSkipped;
  }, [isServerComplete, allSeen, isSkipped]);

  // Mark completion on server
  const markCompleteOnServer = useCallback(async () => {
    try {
      await patch('/users/me', { completeOnboarding: true });
      setIsServerComplete(true);
    } catch {
      // Silent fail - localStorage still tracks state
    }
  }, [patch]);

  // Should show a specific tooltip?
  const shouldShowTooltip = useCallback(
    (id: OnboardingTooltipId): boolean => {
      if (isLoading) return false;
      if (isComplete) return false;
      if (seenTooltips.has(id)) return false;
      return true;
    },
    [isLoading, isComplete, seenTooltips]
  );

  // Dismiss a tooltip
  const dismissTooltip = useCallback(
    (id: OnboardingTooltipId) => {
      setSeenTooltips((prev) => {
        const next = new Set(prev);
        next.add(id);
        persistSeen(next);

        // Check if this was the last one
        const allNowSeen = allTooltipIds.every((tid) => next.has(tid));
        if (allNowSeen) {
          markCompleteOnServer();
        }

        return next;
      });
    },
    [persistSeen, allTooltipIds, markCompleteOnServer]
  );

  // Skip all remaining tooltips
  const skipAll = useCallback(() => {
    setIsSkipped(true);
    try {
      localStorage.setItem(SKIPPED_KEY, 'true');
    } catch {
      // Ignore
    }
    markCompleteOnServer();
  }, [markCompleteOnServer]);

  // Is this the last unseen tooltip?
  const isLastTooltip = useCallback(
    (id: OnboardingTooltipId): boolean => {
      const unseenCount = allTooltipIds.filter(
        (tid) => !seenTooltips.has(tid)
      ).length;
      return unseenCount === 1 && !seenTooltips.has(id);
    },
    [allTooltipIds, seenTooltips]
  );

  return {
    isComplete,
    shouldShowTooltip,
    dismissTooltip,
    skipAll,
    isLastTooltip,
    isLoading,
  };
}
